/**
 * The social sign-in buttons on login and signup: Google (ENG-6165) then
 * Microsoft (ENG-6169), each behind its own flag, under one "or" divider
 * between them and the form. With neither offered, nothing renders.
 */
import Divider from '@mui/material/Divider';
import clsx from 'clsx';
import { useState } from 'react';

import GoogleSignInButton from '@/auth/components/GoogleSignInButton/GoogleSignInButton';
import MicrosoftSignInButton from '@/auth/components/MicrosoftSignInButton/MicrosoftSignInButton';
import {
  SocialProvider,
  isGoogleSignInEnabled,
  isMicrosoftSignInEnabled,
} from '@/auth/utils/socialSignIn';

export interface SocialSignInButtonsProps {
  text: 'continue_with' | 'signup_with';
  /** Receives the provider's ID token. A credential: never log it. */
  onCredential: (provider: SocialProvider, idToken: string) => void;
  /** A provider's own failure before any ID token (the Microsoft popup). */
  onError: (provider: SocialProvider, err: unknown) => void;
  disabled?: boolean;
  /** Where the divider sits: above the buttons (after the form) or below them (before it). */
  dividerPlacement?: 'above' | 'below';
  dividerLabel?: string;
}

/** ENG-6192: under "Two-factor verification" while a provider sign-in is offered. */
export const SOCIAL_TWO_FACTOR_NOTE =
  'Applies when you sign in with your email and password. Google and Microsoft sign-ins use ' +
  'their own verification.';

/** Whether SocialSignInButtons has any button to offer. */
export function isSocialSignInEnabled(): boolean {
  return isGoogleSignInEnabled() || isMicrosoftSignInEnabled();
}

export default function SocialSignInButtons({
  text,
  onCredential,
  onError,
  disabled = false,
  dividerPlacement = 'below',
  dividerLabel = 'or',
}: SocialSignInButtonsProps): JSX.Element | null {
  const google = isGoogleSignInEnabled();
  const microsoft = isMicrosoftSignInEnabled();
  // Google's button shows only once its script loads. Microsoft's shows at
  // once, disabled until MSAL is ready, and goes if MSAL fails to load.
  const [googleReady, setGoogleReady] = useState(false);
  const [microsoftFailed, setMicrosoftFailed] = useState(false);
  if (!google && !microsoft) return null;
  const microsoftShown = microsoft && !microsoftFailed;

  // Below the buttons, centred at 24px: the next field brings its own 16px
  // top margin. Above them, 32px from the buttons and 8px more than whatever
  // sits above (the login button's 24px margin, a signup form's last field).
  const divider = ((google && googleReady) || microsoftShown) && (
    <Divider
      className={clsx(
        'text-on-surface-muted body-1',
        dividerPlacement === 'below' ? 'mb-8 mt-24' : 'mb-32 mt-8'
      )}
    >
      {dividerLabel}
    </Divider>
  );

  return (
    <div className="w-full">
      {dividerPlacement === 'above' && divider}
      {google && (
        <GoogleSignInButton
          text={text}
          disabled={disabled}
          onCredential={(idToken) => onCredential(SocialProvider.Google, idToken)}
          onReadyChange={setGoogleReady}
        />
      )}
      {microsoft && (
        // Spaced from Google's button only once it shows.
        <div className={clsx(google && googleReady && microsoftShown && 'mt-12')}>
          <MicrosoftSignInButton
            text={text}
            disabled={disabled}
            onCredential={(idToken) => onCredential(SocialProvider.Microsoft, idToken)}
            onError={(err) => onError(SocialProvider.Microsoft, err)}
            onLoadFailed={() => setMicrosoftFailed(true)}
          />
        </div>
      )}
      {dividerPlacement === 'below' && divider}
    </div>
  );
}
